import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import {
  LayoutList,
  LayoutGrid,
  Zap,
  ZapOff,
  X,
  Save,
  ListTodoIcon,
} from 'lucide-react';
import Card from '@/components/Card';
import FiltersDisplay, {
  type SelectedFilters,
  type TextSearchField,
} from '@/components/Filters/FiltersDisplay';
import FilterOption from '@/components/Filters/FilterOption';
import SelectInput from '@/components/Input/SelectInput';
import api from '@/services/api';

import {
  HeaderContent,
  SearchPanel as SearchPanelContainer,
  DisplayModeGroup,
  DisplayToggleButton,
  TitleStack,
  EnableDevicesButton,
  DisableDevicesButton,
  ModeEditionChip,
  BulkActionModal,
  ButtonsBox,
  CancelButtonBox,
  OutlinedButton,
  HeaderActionsBox,
  FilterActionButton,
} from '../styles';
import { Button } from '@/components/Button';

export type DisplayMode = 'list' | 'grid';

// Free-text filters, searched one at a time from the text bar and each sent
// as its own query param
const TEXT_FILTERS = [
  'customerName',
  'userPPP',
  'ipAddress',
  'lanMac',
  'wanMac',
  'serialNumber',
  'softwareVersion',
] as const;

// Only matched by the full value, flagged by a hint on the text bar
const EXACT_MATCH_SEARCH: readonly string[] = [
  'ipAddress',
  'serialNumber',
  'softwareVersion',
  'lanMac',
  'wanMac',
];

interface SearchPanelProps {
  filters: SelectedFilters;
  setFilters: Dispatch<SetStateAction<SelectedFilters>>;
  manufacturers: string[];
  models: string[];
  displayMode: DisplayMode;
  onDisplayModeChange: (
    event: React.MouseEvent<HTMLElement>,
    newMode: DisplayMode | null
  ) => void;
  // Table page size, only selectable in list mode
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (size: number) => void;
  // Bulk mode props
  bulkMode: boolean;
  bulkAction: 'enable' | 'disable' | null;
  onBulkAction: (action: 'enable' | 'disable') => void;
  onBulkCancel: () => void;
  onBulkSave: () => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  stagedCount: number;
  isMobile?: boolean;
}

/** Contains the advanced search filters to search equipment */
const SearchPanel: React.FC<SearchPanelProps> = ({
  filters,
  setFilters,
  manufacturers,
  models,
  displayMode,
  onDisplayModeChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  bulkMode,
  onBulkAction,
  onBulkCancel,
  onBulkSave,
  onSelectAll,
  onDeselectAll,
  stagedCount,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [draftManufacturer, setDraftManufacturer] = useState<string | null>(
    null
  );
  // Models of the chosen manufacturer; null lists every model
  const [manufacturerModels, setManufacturerModels] = useState<string[] | null>(
    null
  );

  const textSearchFields = useMemo<TextSearchField[]>(
    () =>
      TEXT_FILTERS.map((name) => ({
        name,
        label: t(`listEquipments.table.${name}`),
        hint: EXACT_MATCH_SEARCH.includes(name)
          ? t('listEquipments.exactMatchHint')
          : undefined,
      })),
    [t]
  );

  const handleDraftChange = useCallback((draft: SelectedFilters) => {
    setDraftManufacturer(
      typeof draft.manufacturer === 'string' ? draft.manufacturer : null
    );
  }, []);

  useEffect(() => {
    if (!draftManufacturer) {
      setManufacturerModels(null);
      return;
    }
    // Drops the response of a manufacturer that was changed meanwhile
    let ignore = false;

    const fetchModels = async () => {
      try {
        const { data } = await api.get<ManufacturerCountType>(
          `/dashboard/${encodeURIComponent(draftManufacturer)}`
        );
        if (ignore) return;
        setManufacturerModels(
          data.models.map((model) => model.modelName).sort()
        );
      } catch (error) {
        if (ignore) return;
        console.error(t('common.error'), error);
        setManufacturerModels(null);
      }
    };

    fetchModels();
    return () => {
      ignore = true;
    };
  }, [draftManufacturer, t]);

  const handleBulkOptionClick = (action: 'enable' | 'disable') => {
    onBulkAction(action);
    setBulkModalOpen(false);
  };

  return (
    <SearchPanelContainer>
      <HeaderContent>
        <TitleStack>
          <Typography variant="h5" fontWeight="600">
            {t('listEquipments.searchTitle')}
          </Typography>
          {bulkMode && (
            <ModeEditionChip
              value={true}
              label={t('listEquipments.bulkEdition').toUpperCase()}
            />
          )}
        </TitleStack>

        <HeaderActionsBox>
          {bulkMode && (
            <>
              <OutlinedButton
                color="error"
                startIcon={<X size={16} />}
                onClick={onBulkCancel}
              >
                {t('common.cancel', 'Cancelar')}
              </OutlinedButton>
              <OutlinedButton onClick={onSelectAll}>
                {t('common.selectAll', 'Selecionar todos')}
              </OutlinedButton>
              <OutlinedButton
                onClick={onDeselectAll}
                sx={{ textTransform: 'none' }}
              >
                {t('common.deselectAll', 'Desmarcar todos')}
              </OutlinedButton>
              <Button
                color="primary"
                size="small"
                startIcon={<Save size={16} />}
                onClick={onBulkSave}
                disabled={stagedCount === 0}
              >
                {t('common.saveChanges', 'Salvar alterações')}
                {stagedCount > 0 && ` (${stagedCount})`}
              </Button>
            </>
          )}
          <FilterActionButton
            variant="text"
            color="primary"
            disabled={bulkMode || isMobile}
            onClick={() => setBulkModalOpen(true)}
            startIcon={<ListTodoIcon size={16} />}
          >
            {t('listEquipments.bulkActions', 'Ações em massa')}
          </FilterActionButton>
          <DisplayModeGroup
            value={displayMode}
            exclusive
            onChange={onDisplayModeChange}
          >
            <DisplayToggleButton value="list" disabled={isMobile}>
              <LayoutList size={16} style={{ marginRight: 6 }} />
              {t('equipment.connectedHosts.displayList', 'Lista')}
            </DisplayToggleButton>
            <DisplayToggleButton value="grid">
              <LayoutGrid size={16} style={{ marginRight: 6 }} />
              {t('equipment.connectedHosts.displayCard', 'Cards')}
            </DisplayToggleButton>
          </DisplayModeGroup>
        </HeaderActionsBox>
      </HeaderContent>

      <FiltersDisplay
        advanced
        filters={filters}
        setFilters={setFilters}
        onDraftChange={handleDraftChange}
        dependents={{ manufacturer: ['modelAlias'] }}
        textSearch={textSearchFields}
        disabled={bulkMode}
        actions={
          displayMode === 'list' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                {t('listEquipments.pagination.itemsPerPage')}
              </Typography>
              <SelectInput
                options={pageSizeOptions.map(String)}
                value={String(pageSize)}
                onChange={(event) =>
                  onPageSizeChange(Number(event.target.value))
                }
                disabled={bulkMode}
                width="90px"
              />
            </Box>
          )
        }
      >
        <FilterOption
          field="select"
          name="manufacturer"
          label={t('listEquipments.table.manufacturer')}
          options={manufacturers}
          selectProps={{ searchable: true }}
          disabled={bulkMode}
          width="300px"
        />
        <FilterOption
          field="select"
          name="modelAlias"
          label={t('listEquipments.table.modelAlias')}
          options={manufacturerModels ?? models}
          selectProps={{ searchable: true }}
          disabled={bulkMode}
          width="300px"
        />
        <FilterOption
          field="toggle"
          name="online"
          options={['online', 'offline']}
          label={[
            t('listEquipments.filter.online'),
            t('listEquipments.filter.offline'),
          ]}
          toggleProps={{ startIcons: ['wifi', 'wifi-off'] }}
          disabled={bulkMode}
        />
        <FilterOption
          field="toggle"
          name="inUse"
          options={['enabled', 'disabled']}
          label={[t('common.activated'), t('common.disabled')]}
          toggleProps={{ startIcons: ['zap', 'zap-off'] }}
          disabled={bulkMode}
        />
      </FiltersDisplay>

      {/* Bulk actions modal */}
      <BulkActionModal
        open={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
      >
        <Card width="auto">
          <Box sx={{ p: 3, minWidth: 360 }}>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 3 }}>
              {t('listEquipments.bulkActions', 'Ações em massa')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
              {t(
                'listEquipments.bulkActionsDescription',
                'Selecione a ação que deseja aplicar aos dispositivos:'
              )}
            </Typography>
            <ButtonsBox>
              <EnableDevicesButton
                startIcon={<Zap size={20} />}
                onClick={() => handleBulkOptionClick('enable')}
              >
                {t(
                  'listEquipments.bulkEnable',
                  'Ativar dispositivos selecionados'
                )}
              </EnableDevicesButton>
              <DisableDevicesButton
                startIcon={<ZapOff size={20} />}
                onClick={() => handleBulkOptionClick('disable')}
              >
                {t(
                  'listEquipments.bulkDisable',
                  'Desativar dispositivos selecionados'
                )}
              </DisableDevicesButton>
            </ButtonsBox>

            <CancelButtonBox>
              <Button
                variant="text"
                onClick={() => setBulkModalOpen(false)}
                sx={{ textTransform: 'none' }}
              >
                {t('common.cancel', 'Cancelar')}
              </Button>
            </CancelButtonBox>
          </Box>
        </Card>
      </BulkActionModal>
    </SearchPanelContainer>
  );
};

export default SearchPanel;
